import { Component } from '@theme/component';

/**
 * Six-step shopping quiz. Answers stay in the current DOM, never in URLs or storage.
 * @extends {Component<{startScreen: HTMLElement, startButton: HTMLButtonElement, introHeading: HTMLElement, experience: HTMLElement, fallback: HTMLElement, steps: HTMLFieldSetElement[], result: HTMLElement, progress: HTMLProgressElement, progressText: HTMLElement, status: HTMLElement, back: HTMLButtonElement, next: HTMLButtonElement, navigation: HTMLElement, story?: HTMLElement, outcome?: HTMLElement, notes?: HTMLElement[], resultHeading?: HTMLElement, visualPanel: HTMLElement, visuals?: HTMLElement[], countdown?: HTMLTimeElement, couponCode?: HTMLInputElement, copyCoupon?: HTMLButtonElement, offerLabel?: HTMLElement, loading?: HTMLElement, loadingText?: HTMLElement}>}
 */
class SlKneeQuiz extends Component {
  requiredRefs = ['startScreen', 'startButton', 'introHeading', 'experience', 'fallback', 'steps', 'result', 'progress', 'progressText', 'status', 'back', 'next', 'navigation', 'visualPanel'];

  #step = -1;
  #loading = false;
  #loadingDone = false;
  /** @type {ReturnType<typeof setTimeout>[]} */
  #loadingTimers = [];
  #deadline = 0;
  #deadlineKey = '';
  #expiredAnnounced = false;
  /** @type {ReturnType<typeof setInterval> | undefined} */
  #countdownInterval;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    // Editor morphs can replace controls: start a fresh, internally consistent quiz.
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopCountdown();
    this.#stopLoading();
  }

  /** The merchant-placed timer block, if any; it carries its own length and expired label. */
  get #offer() {
    return this.querySelector('[data-quiz-demo-offer]');
  }

  #initialize() {
    this.#stopCountdown();
    this.#stopLoading();
    this.#loadingDone = false;
    this.#step = -1;
    this.refs.startButton.hidden = false;
    this.refs.fallback.hidden = true;
    this.#showStep(false);
  }

  /** Open the first question only after the visitor starts the quiz. */
  start() {
    if (this.#step !== -1) return;
    this.#step = 0;
    this.#showStep(true);
  }

  /** Advance only after a selection; keep keyboard focus with the new question. */
  next() {
    if (this.#step < 0 || this.#loading) return;
    const step = this.refs.steps[this.#step];
    if (!step?.querySelector('input:checked')) {
      this.refs.status.textContent = this.dataset.selectionError ?? '';
      step?.setAttribute('aria-invalid', 'true');
      step?.querySelector('input')?.focus({ preventScroll: true });
      return;
    }

    this.#step += 1;
    if (this.#step === this.refs.steps.length) {
      this.#showResult();
      // The calibrating screen plays once per run; reviewing answers goes straight back to the result.
      if (!this.#loadingDone && this.#startLoading()) {
        this.#showStep(true);
        return;
      }
    }
    this.#showStep(true);
  }

  /** Return to a previous answer without clearing it. */
  back() {
    if (this.#loading) return;
    this.#step = Math.max(0, this.#step - 1);
    this.#showStep(true);
  }

  /** Clear all answers, including hidden result content. */
  restart() {
    for (const input of this.querySelectorAll('input')) input.checked = false;
    if (this.refs.story) this.refs.story.textContent = '';
    if (this.refs.outcome) this.refs.outcome.textContent = '';
    this.#stopLoading();
    this.#loadingDone = false;
    this.#step = -1;
    this.#showStep(true);
  }

  /**
   * Exclusive answers cannot coexist with a reported condition.
   * @param {Event} event - Framework-routed native change event.
   */
  changeAnswer(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const step = input.closest('fieldset');
    if (!step) return;

    if (input.type === 'checkbox' && input.checked) {
      for (const other of step.querySelectorAll('input')) {
        if (other === input) continue;
        if (input.dataset.exclusive === 'true' || other.dataset.exclusive === 'true') other.checked = false;
      }
    }

    step.removeAttribute('aria-invalid');
    this.refs.status.textContent = '';
  }

  /** Copy the offer code. The discount itself lives in Shopify; the code is applied at checkout. */
  async copyCoupon() {
    const input = this.refs.couponCode;
    if (!input || this.refs.result.hidden || this.#updateCountdown() === 0) return;

    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(input.value);
      if (this.isConnected && !this.refs.result.hidden) this.refs.status.textContent = this.dataset.copySuccess ?? '';
    } catch {
      if (!this.isConnected || this.refs.result.hidden) return;
      input.focus();
      input.select();
      this.refs.status.textContent = this.dataset.copyFallback ?? '';
    }
  }

  #startCountdown() {
    this.#stopCountdown();
    if (!this.refs.countdown || !this.refs.couponCode) return;
    const key = `sl-quiz-demo:${this.dataset.sectionId}:${this.refs.couponCode.value}`;
    if (this.#deadlineKey !== key || !this.#deadline) {
      this.#deadlineKey = key;
      this.#expiredAnnounced = false;
      const now = Date.now();
      const duration = (Number(this.#offer?.dataset.minutes) || 30) * 60 * 1000;
      let stored = 0;
      try {
        stored = Number(sessionStorage.getItem(key));
      } catch {
        // The quiz also works when browser storage is unavailable.
      }
      this.#deadline = Number.isFinite(stored) && stored > 0 && stored <= now + duration ? stored : now + duration;
      try {
        // Persist only this offer deadline, never the visitor's health answers.
        sessionStorage.setItem(key, String(this.#deadline));
      } catch {
        // Keep the deadline in memory for this component's lifetime instead.
      }
    }
    if (this.#updateCountdown() > 0) this.#countdownInterval = setInterval(() => this.#updateCountdown(), 1000);
  }

  #updateCountdown() {
    const remaining = Math.max(0, Math.ceil((this.#deadline - Date.now()) / 1000));
    if (this.refs.countdown) {
      const minutes = String(Math.floor(remaining / 60)).padStart(2, '0');
      const seconds = String(remaining % 60).padStart(2, '0');
      this.refs.countdown.textContent = `${minutes}:${seconds}`;
      this.refs.countdown.dateTime = `PT${remaining}S`;
    }
    if (this.refs.copyCoupon) this.refs.copyCoupon.disabled = remaining === 0;
    if (remaining === 0) {
      this.#stopCountdown();
      if (this.refs.offerLabel) this.refs.offerLabel.textContent = this.#offer?.dataset.expired ?? '';
      if (!this.#expiredAnnounced) {
        this.refs.status.textContent = this.#offer?.dataset.expired ?? '';
        this.#expiredAnnounced = true;
      }
    }
    return remaining;
  }

  #stopCountdown() {
    if (this.#countdownInterval !== undefined) clearInterval(this.#countdownInterval);
    this.#countdownInterval = undefined;
  }

  #showStep(focus) {
    const intro = this.#step === -1;
    const loading = this.#loading;
    const complete = this.#step === this.refs.steps.length && !loading;
    this.refs.startScreen.hidden = !intro;
    this.refs.experience.hidden = intro;
    for (const [index, step] of this.refs.steps.entries()) {
      step.hidden = intro || complete || loading || index !== this.#step;
      step.removeAttribute('aria-invalid');
    }

    this.refs.result.hidden = !complete;
    if (this.refs.loading) this.refs.loading.hidden = !loading;
    const visualStep = loading ? this.refs.steps.length - 1 : Math.max(0, this.#step);
    let hasVisual = false;
    for (const visual of this.refs.visuals ?? []) {
      visual.hidden = complete || Number(visual.dataset.step) !== visualStep;
      if (!visual.hidden) hasVisual = true;
    }
    if (this.refs.visualPanel) this.refs.visualPanel.hidden = !hasVisual;
    this.classList.toggle('sl-quiz__card--complete', complete);
    this.classList.toggle('sl-quiz__card--loading', loading);
    this.classList.toggle('sl-quiz__card--no-visual', !hasVisual);
    this.refs.navigation.hidden = intro || complete || loading;
    this.refs.back.hidden = this.#step === 0;
    this.refs.next.textContent = (this.#step === this.refs.steps.length - 1 ? this.dataset.resultLabel : this.dataset.nextLabel) ?? '';
    this.refs.status.textContent = '';
    this.refs.progress.value = complete || loading ? this.refs.steps.length : Math.max(0, this.#step);
    this.refs.progressText.textContent = complete || loading
      ? this.dataset.completeLabel ?? ''
      : (this.dataset.stepLabel ?? '').replace('[current]', String(Math.max(0, this.#step) + 1)).replace('[total]', String(this.refs.steps.length));
    if (complete) this.#startCountdown();
    else this.#stopCountdown();

    if (focus) {
      const heading = intro
        ? this.refs.introHeading
        : loading
          ? this.refs.loading
          : complete
            ? this.refs.resultHeading ?? this.refs.result
            : this.refs.steps[this.#step]?.querySelector('legend');
      if (heading instanceof HTMLElement) {
        heading.focus({ preventScroll: true });
        heading.scrollIntoView({ block: 'nearest', behavior: 'instant' });
      }
    }
  }

  /** Show a short "calibrating" screen before the result. Returns false when the merchant turned it off. */
  #startLoading() {
    const panel = this.refs.loading;
    const configured = Number(panel?.dataset.seconds) || 0;
    if (!panel || configured <= 0) return false;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const duration = (reduced ? Math.min(configured, 2) : configured) * 1000;
    /** @type {string[]} */
    let messages = [];
    try {
      messages = JSON.parse(panel.dataset.messages ?? '[]');
    } catch {
      // Keep the first message already in the markup.
    }
    const text = this.refs.loadingText;
    if (text && messages.length > 0) text.textContent = messages[0];

    panel.style.setProperty('--sl-quiz-loading-duration', `${duration}ms`);
    panel.classList.remove('is-running');
    void panel.offsetWidth; // Restart the progress bar animation.
    panel.classList.add('is-running');

    this.#loading = true;
    const interval = duration / Math.max(messages.length, 1);
    for (let index = 1; index < messages.length; index += 1) {
      this.#loadingTimers.push(
        setTimeout(() => {
          if (!text) return;
          text.textContent = messages[index];
          if (!reduced) text.animate([{ opacity: 0, transform: 'translateY(4px)' }, { opacity: 1, transform: 'none' }], { duration: 350 });
        }, interval * index)
      );
    }
    this.#loadingTimers.push(setTimeout(() => this.#finishLoading(), duration));
    return true;
  }

  #finishLoading() {
    this.#stopLoading();
    this.#loadingDone = true;
    this.#showStep(true);
  }

  #stopLoading() {
    for (const timer of this.#loadingTimers) clearTimeout(timer);
    this.#loadingTimers = [];
    this.#loading = false;
    this.refs.loading?.classList.remove('is-running');
  }

  /** Weave the chosen concerns and the one thing the visitor misses into the merchant's message blocks. */
  #showResult() {
    const phrases = [...this.querySelectorAll('input[data-phrase]:checked')].map((input) => input.getAttribute('data-phrase') ?? '');
    const goal = this.querySelector('input[data-goal]:checked');
    const { story, outcome, notes } = this.refs;

    if (story) {
      const list = new Intl.ListFormat(document.documentElement.lang || 'en', { style: 'long', type: 'conjunction' }).format(phrases);
      const template = (phrases.length > 0 ? story.dataset.withConcerns : story.dataset.withoutConcerns) ?? '';
      story.textContent = template.replace('[conditions]', () => list).replace('[activity]', () => goal?.getAttribute('data-activity') ?? '');
      story.hidden = !story.textContent;
    }

    if (outcome) {
      outcome.textContent = goal?.getAttribute('data-result') ?? '';
      outcome.hidden = !outcome.textContent;
    }

    const review = Boolean(this.querySelector('input[data-review="true"]:checked'));
    for (const note of notes ?? []) note.hidden = note.dataset.conditional === 'true' && !review;
  }
}

if (!customElements.get('sl-knee-quiz-component')) {
  customElements.define('sl-knee-quiz-component', SlKneeQuiz);
}
